// Admin sidebar nav. Geometry mirrors apps/dashboard/src/components/
// layout/sidebar-nav.tsx (RailSlot + mx-2 row + active-state cues) so
// the two apps feel identical to a human; the differences are:
//
//   • Groups: Operate / Money / Trust / Platform.
//   • Gating: by staff scope (read from useStaffMe), not plan code.
//   • No external Docs link in P0 — the admin SPA is internal-only.
//
// Scope-gating semantics:
//
//   Each NavItem declares a `requiredScopes` array. A row whose required
//   scopes intersect with the staff member's actual scopes renders
//   normally. A row whose scopes are missing renders disabled (opacity
//   50%, click prevented, tooltip explains the missing scope). The
//   server-side check on the route is the actual enforcement; this is
//   pure UX — no point showing a button that always 403s.

import type { ReactNode } from 'react';
import { NavLink } from 'react-router-dom';
import {
  Activity,
  BarChart3,
  Database,
  GanttChart,
  type LucideIcon,
  ScrollText,
  ShieldAlert,
  Ticket,
  Users,
  UsersRound,
  Wrench,
} from 'lucide-react';
import { Tooltip, TooltipContent, TooltipTrigger } from '../ui/tooltip.js';
import { Separator } from '../ui/separator.js';
import { cn } from '../../lib/utils.js';
import { useStaffMe, type StaffScope } from '../../lib/providers.js';

// Single source of truth for the icon-column geometry. Width / height
// pulled from the CSS variables in index.css so a future tweak is one
// place. Exported for the app-shell footer rows.
export function RailSlot({ children, className }: { children: ReactNode; className?: string }) {
  return (
    <span
      className={cn(
        'flex h-[var(--sidebar-row-h)] w-[var(--sidebar-slot-w)] shrink-0 items-center justify-center',
        className,
      )}
    >
      {children}
    </span>
  );
}

interface NavItem {
  to: string;
  label: string;
  icon: LucideIcon;
  end?: boolean;
  // Row is enabled iff the staff member has AT LEAST ONE of these
  // scopes. Empty array = always enabled (e.g. /me).
  requiredScopes: readonly StaffScope[];
}

interface NavGroup {
  label: string;
  items: NavItem[];
}

const READ_ANY: readonly StaffScope[] = [
  'staff:read',
  'staff:support',
  'staff:finance',
  'staff:platform',
];

const NAV_GROUPS: NavGroup[] = [
  {
    label: 'Operate',
    items: [
      { to: '/', label: 'Overview', icon: BarChart3, end: true, requiredScopes: READ_ANY },
      {
        to: '/tenants',
        label: 'Tenants',
        icon: Users,
        requiredScopes: ['staff:read', 'staff:support', 'staff:platform'],
      },
      {
        to: '/tickets',
        label: 'Tickets',
        icon: Ticket,
        requiredScopes: ['staff:support', 'staff:platform'],
      },
      { to: '/audit', label: 'Audit', icon: ScrollText, requiredScopes: READ_ANY },
    ],
  },
  {
    label: 'Money',
    items: [
      {
        to: '/billing',
        label: 'Billing',
        icon: GanttChart,
        requiredScopes: ['staff:finance', 'staff:platform'],
      },
      {
        to: '/money',
        label: 'Money & Usage',
        icon: Activity,
        requiredScopes: ['staff:finance', 'staff:platform'],
      },
    ],
  },
  {
    label: 'Trust',
    items: [
      {
        to: '/security',
        label: 'Security',
        icon: ShieldAlert,
        requiredScopes: ['staff:support', 'staff:platform'],
      },
      {
        to: '/stripe',
        label: 'Stripe Sync',
        icon: Database,
        requiredScopes: ['staff:finance', 'staff:platform'],
      },
    ],
  },
  {
    label: 'Platform',
    items: [
      { to: '/health', label: 'Health', icon: Wrench, requiredScopes: ['staff:platform'] },
      { to: '/staff', label: 'Staff', icon: UsersRound, requiredScopes: ['staff:platform'] },
    ],
  },
];

interface SidebarNavProps {
  onNavigate?: () => void;
  collapsed?: boolean;
}

export function SidebarNav({ onNavigate, collapsed = false }: SidebarNavProps) {
  const me = useStaffMe();
  const haveScopes = new Set<StaffScope>(me.scopes);

  return (
    <nav className="flex flex-col gap-3 py-3" aria-label="Primary">
      {NAV_GROUPS.map((group, groupIdx) => (
        <div key={group.label} className="flex flex-col gap-0.5">
          {collapsed ? (
            groupIdx > 0 && <Separator className="mx-3 mb-1" aria-hidden />
          ) : (
            <p className="pb-1 pl-5 text-[10px] font-semibold uppercase tracking-wider text-muted-foreground">
              {group.label}
            </p>
          )}
          {group.items.map((item) => {
            const enabled =
              item.requiredScopes.length === 0 ||
              item.requiredScopes.some((s) => haveScopes.has(s));
            const reasonProp =
              !enabled
                ? { disabledReason: `Requires ${item.requiredScopes.join(' or ')}` }
                : {};
            return (
              <NavRow
                key={item.to}
                to={item.to}
                label={item.label}
                Icon={item.icon}
                end={item.end ?? false}
                collapsed={collapsed}
                onNavigate={onNavigate}
                disabled={!enabled}
                {...reasonProp}
              />
            );
          })}
        </div>
      ))}
    </nav>
  );
}

interface NavRowProps {
  to: string;
  label: string;
  Icon: LucideIcon;
  end: boolean;
  collapsed: boolean;
  onNavigate: (() => void) | undefined;
  disabled?: boolean;
  disabledReason?: string;
}

function NavRow({
  to,
  label,
  Icon,
  end,
  collapsed,
  onNavigate,
  disabled = false,
  disabledReason,
}: NavRowProps) {
  if (disabled) {
    const row = (
      <div
        className={cn(
          'group relative mx-2 flex h-[var(--sidebar-row-h)] cursor-not-allowed items-center rounded-md text-sm opacity-50',
          'text-muted-foreground',
        )}
        aria-disabled="true"
      >
        <RailSlot>
          <Icon
            aria-hidden
            className="h-[var(--sidebar-icon-size)] w-[var(--sidebar-icon-size)]"
          />
        </RailSlot>
        <span className={cn('pr-3', collapsed ? 'sr-only' : 'whitespace-nowrap')}>
          {label}
        </span>
      </div>
    );
    return (
      <Tooltip>
        <TooltipTrigger asChild>{row}</TooltipTrigger>
        <TooltipContent side="right">
          {disabledReason ?? `${label} requires elevated staff scope`}
        </TooltipContent>
      </Tooltip>
    );
  }

  const link = (
    <NavLink
      to={to}
      end={end}
      onClick={onNavigate}
      className={({ isActive }) =>
        cn(
          'group relative mx-2 flex h-[var(--sidebar-row-h)] items-center rounded-md text-sm transition-colors',
          'focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2 focus-visible:ring-offset-card',
          isActive
            ? 'bg-accent text-foreground font-medium'
            : 'text-muted-foreground hover:bg-accent/40 hover:text-foreground',
        )
      }
    >
      {({ isActive }) => (
        <>
          <span
            aria-hidden
            className={cn(
              'pointer-events-none absolute -left-2 top-1.5 bottom-1.5 w-[3px] rounded-r-full bg-primary transition-opacity duration-150',
              isActive ? 'opacity-100' : 'opacity-0',
            )}
          />
          <RailSlot>
            <Icon
              aria-hidden
              className={cn(
                'h-[var(--sidebar-icon-size)] w-[var(--sidebar-icon-size)] transition-all',
                isActive ? 'text-primary' : 'opacity-70 group-hover:opacity-100',
              )}
            />
          </RailSlot>
          <span className={cn('pr-3', collapsed ? 'sr-only' : 'whitespace-nowrap')}>
            {label}
          </span>
        </>
      )}
    </NavLink>
  );

  if (collapsed) {
    return (
      <Tooltip>
        <TooltipTrigger asChild>{link}</TooltipTrigger>
        <TooltipContent side="right">{label}</TooltipContent>
      </Tooltip>
    );
  }
  return link;
}
